/* La Sura - reference photos on the bespoke enquiry (DECISIONS 2026-09-30).

   Shopify's contact form can't take files, but the basket can. Each photo
   is uploaded as a line-item property on a £0 unlisted product, which gives
   back a lasting cdn.shopify.com link; the line is removed straight away by
   its own key, and the link goes into the enquiry as a hidden
   contact[Reference photo N] field, so the kitchen gets it in the email.

   Only this script's own lines are ever touched, by key; the rest of the
   shopper's basket is left alone and no basket events are fired, so the
   drawer and count don't flicker. The section only renders this element
   when the chosen product's variant is available and priced at £0, so a
   line that failed to come out costs nothing.

   Without JavaScript the element stays hidden and the email note shows, as
   before. Photos upload as soon as they're picked; sending the form waits
   for any still on their way. */
(() => {
  if (customElements.get('lasura-enquiry-photos')) return;

  const root = () => window.Shopify?.routes?.root || '/';
  const fill = (text, values) => Object.entries(values).reduce((t, [k, v]) => t.split(`{${k}}`).join(v), text || '');
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };

  class LasuraEnquiryPhotos extends HTMLElement {
    connectedCallback() {
      if (this.ready || !window.fetch || !window.FormData) return;
      this.ready = true;
      this.pending = new Set();
      this.input = this.querySelector('[data-photo-input]');
      this.addButton = this.querySelector('[data-photo-add]');
      this.list = this.querySelector('[data-photo-list]');
      this.status = this.querySelector('[data-photo-status]');
      this.form = this.closest('form');
      this.hidden = false;
      this.closest('fieldset')?.querySelector('[data-photos-fallback]')?.setAttribute('hidden', '');

      this.addButton.addEventListener('click', () => this.input.click());
      this.input.addEventListener('change', () => {
        [...this.input.files].forEach((file) => this.add(file));
        this.input.value = '';
      });
      /* Capture, so it runs before the wizard's own submit check. */
      this.form?.addEventListener('submit', (event) => this.onSubmit(event), true);
      this.sync();
    }

    get max() {
      return Number(this.dataset.max) || 4;
    }

    say(text) {
      this.status.textContent = text;
    }

    add(file) {
      if (this.list.children.length >= this.max) return this.say(fill(this.dataset.full, { max: this.max }));
      if (!file.type.startsWith('image/')) return this.say(this.dataset.notImage);
      const limit = Number(this.dataset.maxMb) || 10;
      if (file.size > limit * 1024 * 1024) return this.say(fill(this.dataset.tooBig, { size: limit }));

      const item = document.createElement('li');
      item.className = 'lasura-enquiry__photo';
      const img = document.createElement('img');
      img.alt = '';
      img.width = img.height = 72;
      img.src = URL.createObjectURL(file);
      const text = document.createElement('span');
      text.className = 'lasura-enquiry__photo-text';
      const name = document.createElement('span');
      name.className = 'lasura-enquiry__photo-name';
      name.textContent = file.name;
      const state = document.createElement('span');
      state.className = 'lasura-enquiry__photo-state';
      state.textContent = this.dataset.uploading;
      text.append(name, state);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lasura-enquiry__photo-remove';
      remove.textContent = this.dataset.remove;
      remove.setAttribute('aria-label', `${this.dataset.remove}: ${file.name}`);
      remove.addEventListener('click', () => this.drop(item, img));
      item.append(img, text, remove);
      this.list.append(item);
      this.say(fill(this.dataset.uploadingOne, { name: file.name }));
      this.sync();

      const job = this.upload(file)
        .then((url) => {
          if (!item.isConnected) return;
          const field = document.createElement('input');
          field.type = 'hidden';
          field.value = url;
          field.dataset.photoLink = '';
          item.append(field);
          state.textContent = this.dataset.done;
          this.say(fill(this.dataset.doneOne, { name: file.name }));
        })
        .catch(() => {
          if (!item.isConnected) return;
          item.dataset.failed = '';
          state.textContent = this.dataset.failed;
          this.say(fill(this.dataset.failedOne, { name: file.name }));
        })
        .finally(() => {
          this.pending.delete(job);
          this.sync();
        });
      this.pending.add(job);
    }

    async upload(file) {
      const body = new FormData();
      body.append('id', this.dataset.variant);
      body.append('quantity', '1');
      body.append('properties[Photo]', file);
      body.append('properties[_lasura_enquiry]', `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`);
      const response = await fetch(`${root()}cart/add.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('upload');
      const line = await response.json();
      const url = line.properties?.Photo;
      await this.takeOut(line.key);
      if (typeof url !== 'string' || !url.startsWith('https://')) throw new Error('link');
      return url;
    }

    /* Removes our line by its key, once more if the first try fails. */
    async takeOut(key) {
      if (!key) return;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const response = await fetch(`${root()}cart/change.js`, { method: 'POST', headers: json, body: JSON.stringify({ id: key, quantity: 0 }) });
          if (response.ok) return;
        } catch (error) {
          /* try again */
        }
      }
    }

    drop(item, img) {
      URL.revokeObjectURL(img.src);
      item.remove();
      this.say(this.dataset.removed);
      this.sync();
      this.addButton.focus();
    }

    /* Numbers the links 1, 2, 3 in the order shown, and hides Add at the limit. */
    sync() {
      [...this.list.querySelectorAll('[data-photo-link]')].forEach((field, i) => (field.name = `contact[Reference photo ${i + 1}]`));
      this.addButton.hidden = this.list.children.length >= this.max;
    }

    async onSubmit(event) {
      if (!this.pending.size) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.say(this.dataset.waiting);
      await Promise.allSettled([...this.pending]);
      this.form.requestSubmit();
    }
  }

  customElements.define('lasura-enquiry-photos', LasuraEnquiryPhotos);
})();
